import React, { useState, useEffect, useRef } from 'react';
import { Zap, Wifi, WifiOff, CheckCircle, XCircle, RefreshCw, MapPin, QrCode, Settings, Shield, Smartphone, Copy, ChevronRight, ChevronLeft, AlertTriangle } from 'lucide-react';

const ChargerOnboarding = ({ onComplete, onCancel }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Step 1: Charger details
  const [chargerDetails, setChargerDetails] = useState({
    charger_type: 'public',
    name: '',
    serial_number: '',
    location: '',
    latitude: '',
    longitude: '',
    power_rating: '22000',
    connector_type: 'CCS2',
    price_per_kwh: '0.35'
  });

  // Address -> coordinates lookup
  const [geocoding, setGeocoding] = useState(false);
  const [geocodeMessage, setGeocodeMessage] = useState(null); // { type: 'success'|'error', text }

  const handleLookupCoordinates = async () => {
    if (!chargerDetails.location.trim()) {
      setGeocodeMessage({ type: 'error', text: 'Enter a location/address first' });
      return;
    }
    setGeocoding(true);
    setGeocodeMessage(null);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/admin/geocode?address=${encodeURIComponent(chargerDetails.location)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setChargerDetails(prev => ({ ...prev, latitude: data.latitude.toFixed(6), longitude: data.longitude.toFixed(6) }));
        setGeocodeMessage({ type: 'success', text: `Found: ${data.formatted_address}` });
      } else {
        setGeocodeMessage({ type: 'error', text: data.error || 'Could not find that location' });
      }
    } catch (err) {
      setGeocodeMessage({ type: 'error', text: 'Lookup failed — check your connection' });
    }
    setGeocoding(false);
  };

  // Step 2-3: Provisioning result
  const [provisionResult, setProvisionResult] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState({ connected: false });
  const [pollCount, setPollCount] = useState(0);
  const pollRef = useRef(null);

  // Step 4: Configuration result
  const [configResults, setConfigResults] = useState([]);

  // Step 5: QR Code
  const [qrCode, setQrCode] = useState('');

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  // Step 1: Submit charger details and create record
  const handleProvision = async () => {
    setLoading(true);
    setError('');
    try {
      if (!chargerDetails.serial_number || !chargerDetails.name) {
        setError('Serial number and name are required');
        setLoading(false);
        return;
      }
      if (chargerDetails.charger_type === 'public' && !chargerDetails.location) {
        setError('Location is required for a public network charger');
        setLoading(false);
        return;
      }

      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/chargers/provision', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(chargerDetails)
      });

      const data = await response.json();
      if (data.success) {
        setProvisionResult(data);
        setQrCode(data.qrCode);
        setStep(2);
      } else {
        setError(data.error || 'Failed to create charger');
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Start polling for connection
  const startPolling = () => {
    setStep(3);
    setPollCount(0);
    
    if (pollRef.current) clearInterval(pollRef.current);
    
    pollRef.current = setInterval(async () => {
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(`/api/chargers/${provisionResult.charger.id}/connection-status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        
        setConnectionStatus(data);
        setPollCount(prev => prev + 1);
        
        if (data.connected) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      } catch (e) {
        console.error('Poll error:', e);
      }
    }, 3000);
  };

  // Step 4: Auto-configure the connected charger
  const handleConfigure = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/chargers/${provisionResult.charger.id}/auto-configure`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setConfigResults(data.results || []);
        setStep(5);
      } else {
        setError(data.error || 'Configuration failed');
      }
    } catch (err) {
      setError('Failed to configure: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('Copied to clipboard');
    }).catch(() => {
      prompt('Copy this URL:', text);
    });
  };

  const StepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {[
        { num: 1, label: 'Details' },
        { num: 2, label: 'Setup' },
        { num: 3, label: 'Connect' },
        { num: 4, label: 'Configure' },
        { num: 5, label: 'Complete' }
      ].map((s, i) => (
        <React.Fragment key={s.num}>
          <div className="flex flex-col items-center">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
              step > s.num ? 'bg-green-500 text-white' :
              step === s.num ? 'bg-blue-600 text-white' :
              'bg-gray-200 text-gray-500'
            }`}>
              {step > s.num ? <CheckCircle className="w-5 h-5" /> : s.num}
            </div>
            <span className={`text-xs mt-1 ${step >= s.num ? 'text-gray-900 font-medium' : 'text-gray-400'}`}>{s.label}</span>
          </div>
          {i < 4 && <div className={`w-12 h-0.5 mx-2 mt-[-12px] ${step > s.num ? 'bg-green-500' : 'bg-gray-200'}`} />}
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Add New Charger</h2>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
            <XCircle className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          <StepIndicator />

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
              <AlertTriangle className="w-5 h-5 text-red-600 mr-2 shrink-0" />
              <span className="text-sm text-red-800">{error}</span>
            </div>
          )}

          {/* STEP 1: Charger Details */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200 mb-6">
                <p className="text-sm text-blue-800">Enter the charger details below. The serial number must match the ID printed on your BSEC unit — this is how the charger identifies itself to the server.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Charger Type *</label>
                <div className="grid grid-cols-2 gap-3">
                  <button type="button"
                    onClick={() => setChargerDetails({ ...chargerDetails, charger_type: 'public' })}
                    className={`text-left px-4 py-3 rounded-lg border-2 transition-colors ${chargerDetails.charger_type === 'public' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
                    <div className="font-medium text-gray-900">⚡ Public Network</div>
                    <div className="text-xs text-gray-500 mt-0.5">Kerb-side station, shows on the public map, guest QR charging</div>
                  </button>
                  <button type="button"
                    onClick={() => setChargerDetails({ ...chargerDetails, charger_type: 'home' })}
                    className={`text-left px-4 py-3 rounded-lg border-2 transition-colors ${chargerDetails.charger_type === 'home' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
                    <div className="font-medium text-gray-900">🔌 Home Charger</div>
                    <div className="text-xs text-gray-500 mt-0.5">Private unit, claimed by a customer via serial number in the app, never public</div>
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Charger Name *</label>
                  <input type="text" value={chargerDetails.name}
                    onChange={(e) => setChargerDetails({...chargerDetails, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g. BSEC-22k Unit 2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Serial Number *</label>
                  <input type="text" value={chargerDetails.serial_number}
                    onChange={(e) => setChargerDetails({...chargerDetails, serial_number: e.target.value.toUpperCase()})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono"
                    placeholder="e.g. BSEC251471" />
                </div>
              </div>

              {chargerDetails.charger_type === 'public' ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Location / Address *</label>
                    <div className="flex gap-2">
                      <input type="text" value={chargerDetails.location}
                        onChange={(e) => setChargerDetails({...chargerDetails, location: e.target.value})}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="e.g. 14 High Street, Stevenage" />
                      <button type="button" onClick={handleLookupCoordinates} disabled={geocoding}
                        className="px-4 py-2 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg text-sm font-medium hover:bg-blue-100 disabled:opacity-50 whitespace-nowrap">
                        {geocoding ? 'Looking up...' : 'Look up coordinates'}
                      </button>
                    </div>
                    {geocodeMessage && (
                      <p className={`text-xs mt-1 ${geocodeMessage.type === 'error' ? 'text-red-600' : 'text-green-600'}`}>{geocodeMessage.text}</p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Latitude</label>
                      <input type="number" step="0.000001" value={chargerDetails.latitude}
                        onChange={(e) => setChargerDetails({...chargerDetails, latitude: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="51.9020" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Longitude</label>
                      <input type="number" step="0.000001" value={chargerDetails.longitude}
                        onChange={(e) => setChargerDetails({...chargerDetails, longitude: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="-0.2024" />
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 -mt-2">Coordinates can still be entered/adjusted manually — useful for kerb-precise placement.</p>
                </>
              ) : (
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <p className="text-sm text-gray-600">Home chargers aren't shown on the public map, so a precise location isn't needed here — the customer's own address stays with them, not this system. This unit will sit as unclaimed inventory until a customer registers its serial number from the app.</p>
                </div>
              )}

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Power (kW)</label>
                  <select value={chargerDetails.power_rating}
                    onChange={(e) => setChargerDetails({...chargerDetails, power_rating: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                    <option value="7000">7 kW</option>
                    <option value="22000">22 kW</option>
                    <option value="50000">50 kW</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Connector</label>
                  <select value={chargerDetails.connector_type}
                    onChange={(e) => setChargerDetails({...chargerDetails, connector_type: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                    <option value="CCS2">CCS2</option>
                    <option value="Type 2">Type 2</option>
                    <option value="CHAdeMO">CHAdeMO</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Price (£/kWh)</label>
                  <input type="number" step="0.01" value={chargerDetails.price_per_kwh}
                    onChange={(e) => setChargerDetails({...chargerDetails, price_per_kwh: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0.35" />
                </div>
              </div>

              <div className="pt-4 flex justify-end">
                <button onClick={handleProvision} disabled={loading}
                  className="flex items-center px-6 py-3 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50">
                  {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <ChevronRight className="w-4 h-4 mr-2" />}
                  {loading ? 'Creating...' : 'Create & Continue'}
                </button>
              </div>
            </div>
          )}

          {/* STEP 2: WOW Charge Setup Instructions */}
          {step === 2 && provisionResult && (
            <div className="space-y-6">
              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <div className="flex items-center mb-2">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                  <span className="font-medium text-green-900">Charger record created</span>
                </div>
                <p className="text-sm text-green-700">"{provisionResult.charger.name}" ({provisionResult.charger.serial_number}) has been added to the system.</p>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Smartphone className="w-5 h-5 mr-2 text-blue-600" />
                  Configure with WOW Charge App
                </h3>

                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 font-bold text-sm shrink-0 mt-0.5">1</div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900">Open the WOW Charge app on your phone</p>
                      <p className="text-xs text-gray-500">Make sure Bluetooth is enabled and you're near the charger</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 font-bold text-sm shrink-0 mt-0.5">2</div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900">Connect to the charger via Bluetooth</p>
                      <p className="text-xs text-gray-500">Select "{chargerDetails.serial_number}" from the device list</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 font-bold text-sm shrink-0 mt-0.5">3</div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900">Set the OCPP Server URL to:</p>
                      <div className="mt-2 flex items-center bg-gray-900 text-green-400 rounded-lg px-4 py-3 font-mono text-sm">
                        <span className="break-all flex-1">{provisionResult.ocppUrl}</span>
                        <button onClick={() => copyToClipboard(provisionResult.ocppUrl)}
                          className="ml-3 p-1.5 bg-gray-700 rounded hover:bg-gray-600 shrink-0"
                          title="Copy URL">
                          <Copy className="w-4 h-4 text-gray-300" />
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 font-bold text-sm shrink-0 mt-0.5">4</div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900">Save the configuration and power cycle the charger</p>
                      <p className="text-xs text-gray-500">The charger will attempt to connect to the server</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="pt-2 flex justify-between">
                <button onClick={() => setStep(1)}
                  className="flex items-center px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800">
                  <ChevronLeft className="w-4 h-4 mr-1" /> Back
                </button>
                <button onClick={startPolling}
                  className="flex items-center px-6 py-3 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700">
                  <Wifi className="w-4 h-4 mr-2" />
                  I've configured the charger — check connection
                </button>
              </div>
            </div>
          )}

          {/* STEP 3: Waiting for Connection */}
          {step === 3 && (
            <div className="space-y-6">
              <div className="text-center py-8">
                {connectionStatus.connected ? (
                  <div>
                    <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-4">
                      <Wifi className="w-10 h-10 text-green-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-green-900 mb-2">Charger Connected!</h3>
                    <p className="text-sm text-gray-600">"{chargerDetails.serial_number}" is now connected to the OCPP server and communicating.</p>
                    <div className="mt-4 inline-flex items-center px-4 py-2 bg-green-50 rounded-lg border border-green-200">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2" />
                      <span className="text-sm text-green-800">Status: {connectionStatus.status || 'Connected'}</span>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="w-20 h-20 mx-auto bg-blue-100 rounded-full flex items-center justify-center mb-4">
                      <RefreshCw className="w-10 h-10 text-blue-600 animate-spin" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Waiting for connection...</h3>
                    <p className="text-sm text-gray-600 mb-2">Listening for "{chargerDetails.serial_number}" on the OCPP server.</p>
                    <p className="text-xs text-gray-400">Checking every 3 seconds... ({pollCount} checks)</p>
                    
                    {pollCount > 20 && (
                      <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg text-left">
                        <div className="flex items-center mb-2">
                          <AlertTriangle className="w-4 h-4 text-amber-600 mr-2" />
                          <span className="text-sm font-medium text-amber-900">Taking longer than expected?</span>
                        </div>
                        <ul className="text-xs text-amber-700 space-y-1 ml-6 list-disc">
                          <li>Check the OCPP URL is exactly: {provisionResult?.ocppUrl}</li>
                          <li>Ensure the charger has internet connectivity (WiFi/Ethernet/4G)</li>
                          <li>Try power cycling the charger</li>
                          <li>Check that port 9000 is open in your AWS security group</li>
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="pt-2 flex justify-between">
                <button onClick={() => { if (pollRef.current) clearInterval(pollRef.current); setStep(2); }}
                  className="flex items-center px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800">
                  <ChevronLeft className="w-4 h-4 mr-1" /> Back to setup
                </button>
                {connectionStatus.connected && (
                  <button onClick={() => setStep(4)}
                    className="flex items-center px-6 py-3 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700">
                    <Settings className="w-4 h-4 mr-2" />
                    Configure charger
                  </button>
                )}
              </div>
            </div>
          )}

          {/* STEP 4: Auto-Configuration */}
          {step === 4 && (
            <div className="space-y-6">
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <h3 className="text-lg font-semibold text-blue-900 mb-2 flex items-center">
                  <Settings className="w-5 h-5 mr-2" />
                  Charger Configuration
                </h3>
                <p className="text-sm text-blue-700">The following OCPP settings will be applied to secure and optimise your charger:</p>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg divide-y">
                {[
                  { key: 'HeartbeatInterval', value: '60s', desc: 'How often the charger reports it\'s online' },
                  { key: 'MeterValueSampleInterval', value: '30s', desc: 'How often energy readings are sent during charging' },
                  { key: 'ConnectionTimeOut', value: '120s', desc: 'Max time to wait for a charging session to start' },
                  { key: 'StopTransactionOnInvalidId', value: 'Yes', desc: 'Stop charging if the user ID becomes invalid' },
                  { key: 'StopTransactionOnEVSideDisconnect', value: 'Yes', desc: 'Stop charging if the cable is disconnected' },
                  { key: 'UnlockConnectorOnEVSideDisconnect', value: 'Yes', desc: 'Unlock the connector when the EV disconnects' }
                ].map((setting, i) => (
                  <div key={i} className="flex items-center justify-between px-4 py-3">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{setting.key}</p>
                      <p className="text-xs text-gray-500">{setting.desc}</p>
                    </div>
                    <span className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">{setting.value}</span>
                  </div>
                ))}
              </div>

              <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                <div className="flex items-center">
                  <Shield className="w-5 h-5 text-amber-600 mr-2" />
                  <span className="text-sm font-medium text-amber-900">Security note</span>
                </div>
                <p className="text-xs text-amber-700 mt-1">These settings ensure the charger operates safely. Additional security lockdown (disabling local configuration access) should be done via the WOW Charge app's Bluetooth settings.</p>
              </div>

              <div className="pt-2 flex justify-end">
                <button onClick={handleConfigure} disabled={loading}
                  className="flex items-center px-6 py-3 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50">
                  {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Settings className="w-4 h-4 mr-2" />}
                  {loading ? 'Configuring...' : 'Apply Configuration'}
                </button>
              </div>
            </div>
          )}

          {/* STEP 5: Complete */}
          {step === 5 && (
            <div className="space-y-6">
              <div className="text-center py-4">
                <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Charger Ready!</h3>
                <p className="text-sm text-gray-600">"{chargerDetails.name}" has been provisioned, connected, and configured.</p>
              </div>

              {/* Summary */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <h4 className="font-medium text-gray-900 mb-3">Summary</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-gray-500">Name:</span> <span className="font-medium">{chargerDetails.name}</span></div>
                  <div><span className="text-gray-500">Serial:</span> <span className="font-mono font-medium">{chargerDetails.serial_number}</span></div>
                  <div><span className="text-gray-500">Location:</span> <span className="font-medium">{chargerDetails.location}</span></div>
                  <div><span className="text-gray-500">Power:</span> <span className="font-medium">{parseInt(chargerDetails.power_rating) / 1000}kW {chargerDetails.connector_type}</span></div>
                  <div><span className="text-gray-500">Price:</span> <span className="font-medium">£{chargerDetails.price_per_kwh}/kWh</span></div>
                  <div><span className="text-gray-500">Status:</span> <span className="font-medium text-green-600">Online & Configured</span></div>
                </div>
              </div>

              {/* QR Code */}
              {qrCode && (
                <div className="bg-white border border-gray-200 rounded-lg p-6 text-center">
                  <h4 className="font-medium text-gray-900 mb-3 flex items-center justify-center">
                    <QrCode className="w-5 h-5 mr-2" />
                    Customer QR Code
                  </h4>
                  <img src={qrCode} alt="Charger QR Code" className="w-48 h-48 mx-auto border-4 border-white shadow-lg rounded-lg" />
                  <p className="text-xs text-gray-500 mt-3">Print and attach this QR code to the charger. Customers scan it with the LMPEnergy app to start charging.</p>
                  <button
                    onClick={() => {
                      var link = document.createElement('a');
                      link.download = 'LMPEnergy-QR-' + chargerDetails.serial_number + '.png';
                      link.href = qrCode;
                      link.click();
                    }}
                    className="mt-3 inline-flex items-center px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100"
                  >
                    Download QR Code
                  </button>
                </div>
              )}

              {/* Config results */}
              {configResults.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <h4 className="font-medium text-gray-900 mb-2 text-sm">Configuration Results</h4>
                  <div className="space-y-1">
                    {configResults.map((r, i) => (
                      <div key={i} className="flex items-center text-xs">
                        {r.result?.success ?
                          <CheckCircle className="w-3 h-3 text-green-500 mr-2" /> :
                          <XCircle className="w-3 h-3 text-red-500 mr-2" />
                        }
                        <span className="font-mono text-gray-600">{r.key}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="pt-4 flex justify-center">
                <button onClick={() => onComplete && onComplete()}
                  className="flex items-center px-8 py-3 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Done — Return to Stations
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChargerOnboarding;
